/**
 * Resolve the clinic OWNER's doctor_id for a given clinic_id.
 *
 * Packages (and several other catalog-style resources like services, referral
 * sources, etc.) are stored under the clinic owner's doctor profile. Linked
 * (child) doctors in the same clinic should share the owner's catalog instead
 * of using their own (usually empty) list.
 *
 * Strategy:
 *   1. Use `clinics.owner_doctor_id` when the clinic explicitly defines it.
 *   2. Otherwise find every linked doctor and apply the legacy `is_owner` rule.
 *   3. Fall back to the provided current doctor_id if no owner row exists
 *      (e.g. legacy single-doctor clinics where `is_owner` was never written).
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const ownerCache = new Map<string, string>();

export async function getClinicOwnerDoctorId(
  clinicId: string | null | undefined,
  fallbackDoctorId?: string | null
): Promise<string> {
  const fallback = (fallbackDoctorId || '').trim();
  const cid = (clinicId || '').trim();
  if (!cid) return fallback;

  if (ownerCache.has(cid)) {
    return ownerCache.get(cid) as string;
  }

  try {
    const clinicSnap = await getDoc(doc(db, 'clinics', cid));
    const explicitOwnerId = clinicSnap.exists()
      ? String(clinicSnap.data()?.owner_doctor_id || '').trim()
      : '';
    if (explicitOwnerId) {
      ownerCache.set(cid, explicitOwnerId);
      return explicitOwnerId;
    }

    // If the current doctor is themselves the owner, no extra lookup needed.
    if (fallback) {
      const ownSnap = await getDoc(doc(db, 'doctors', fallback));
      if (ownSnap.exists() && ownSnap.data()?.is_owner === true) {
        ownerCache.set(cid, fallback);
        return fallback;
      }
    }

    const dcaSnap = await getDocs(
      query(
        collection(db, 'doctor_clinic_assistant'),
        where('clinic_id', '==', cid)
      )
    );
    const doctorIds: string[] = Array.from(
      new Set(
        dcaSnap.docs
          .map((d) => (d.data().doctor_id as string) || '')
          .filter(Boolean)
      )
    );

    if (doctorIds.length === 0) {
      if (fallback) ownerCache.set(cid, fallback);
      return fallback;
    }

    const doctorSnaps = await Promise.all(
      doctorIds.map((id) => getDoc(doc(db, 'doctors', id)))
    );
    for (let i = 0; i < doctorSnaps.length; i += 1) {
      const snap = doctorSnaps[i];
      if (snap.exists() && snap.data()?.is_owner === true) {
        const ownerId = doctorIds[i];
        ownerCache.set(cid, ownerId);
        return ownerId;
      }
    }

    if (fallback) ownerCache.set(cid, fallback);
    return fallback;
  } catch (err) {
    console.error('[clinicOwnerDoctor] Failed to resolve owner doctor:', err);
    return fallback;
  }
}
